import { api } from '@/api/client';

/** A project fase = a parent planning task, with the earliest child date_deadline. */
export interface DashboardFase {
  id: number;
  name: string;
  date_deadline: string;
}

export interface ProjectFasesResponse {
  fases: DashboardFase[];
  /**
   * collectorId → faseId (materiaallijst-based, with date-proximity fallback).
   * NB: over JSON the keys are strings; access must stay index-based
   * (`collectorFases[collectorId]`, which coerces) — do not compare Object.keys
   * entries to a numeric faseId without parsing.
   */
  collectorFases: Record<number, number>;
  /**
   * Werf-level "collectoren al geleverd" flag: true when any fase task carries
   * the PL-set x_studio_collectoren_geleverd boolean. Toggle via
   * setCollectorenGeleverd.
   */
  collectorenGeleverd: boolean;
}

export async function fetchProjectFases(projectId: number): Promise<ProjectFasesResponse> {
  return api<ProjectFasesResponse>(`/api/projects/${projectId}/fases`);
}

/**
 * Flip the werf-level "collectoren geleverd" flag. Writes the fase-level boolean
 * across all fase heads server-side; returns the new value.
 */
export async function setCollectorenGeleverd(
  projectId: number,
  geleverd: boolean,
): Promise<{ collectorenGeleverd: boolean }> {
  return api<{ collectorenGeleverd: boolean }>(
    `/api/projects/${projectId}/collectoren-geleverd`,
    { method: 'PUT', body: JSON.stringify({ geleverd }) },
  );
}

/**
 * Create a new fase on a project. The backend creates it in the "Fase Indeling"
 * stage so Odoo auto-creates the first planning child; the returned fase has an
 * empty date_deadline until that child is scheduled.
 */
export async function createProjectFase(projectId: number, name: string): Promise<DashboardFase> {
  return api<DashboardFase>(`/api/projects/${projectId}/fases`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

/**
 * Rename a fase. Validation matches create exactly (trimmed, 1–200 chars); the
 * fase name is nowhere a persisted key, so the new name propagates by itself to
 * the tab bar, the planning-day labels and the onderaannemer display labels.
 */
export async function renameProjectFase(
  projectId: number,
  faseId: number,
  name: string,
): Promise<{ id: number; name: string }> {
  return api<{ id: number; name: string }>(`/api/projects/${projectId}/fases/${faseId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

/**
 * Why a fase may not be deleted. `message` is server-authored Dutch — show it
 * as-is. NB: real planning children are no longer a blocker (Igor, 2026-09-08);
 * they are deleted with the fase, which is what `plannedChildren` warns about.
 * Logged hours ARE a blocker (Amendment 2, same day): the hours survive an
 * unlink but their per-day attribution does not.
 */
export interface FaseDeleteBlocker {
  code:
    | 'COLLECTORS_PRESENT'
    | 'MATERIAALLIJST_PRESENT'
    | 'VORDERING_BOOKED'
    | 'COLLECTOR_CHILD_PRESENT'
    | 'TIMESHEET_PRESENT';
  message: string;
}

/**
 * A scheduled werf-day the delete will destroy. `date` is `YYYY-MM-DD`, or null
 * for a child that is real work but has no date yet.
 */
export interface FasePlanningDay {
  id: number;
  date: string | null;
}

export interface FaseDeletability {
  deletable: boolean;
  blockers: FaseDeleteBlocker[];
  /**
   * Fase-bestanden hanging off the fase. They are KEPT (the fase is archived,
   * not destroyed); the dialog says so. -1 = could not be determined.
   */
  fileCount: number;
  /** The planning days the delete cascades into. Empty for an untouched fase. */
  plannedChildren: FasePlanningDay[];
}

/**
 * Delete preflight: may this fase go, and what goes with it. Advisory only —
 * the DELETE endpoint recomputes the same assessment and is the real gate.
 */
export async function fetchFaseDeletability(
  projectId: number,
  faseId: number,
): Promise<FaseDeletability> {
  return api<FaseDeletability>(`/api/projects/${projectId}/fases/${faseId}/deletable`);
}

/** What the server archived, so a caller can report the cascade size. */
export interface FaseArchiveResult {
  id: number;
  childrenArchived: number;
  plannedDaysArchived: number;
}

/**
 * Remove a fase and its planning children from the board. The server ARCHIVES
 * them (`active = false`) rather than unlinking — the fase stays findable in
 * Odoo — but the endpoint keeps its DELETE verb, matching the Dutch the rest of
 * the planning UI uses for the same mechanism.
 *
 * A fase with collectoren, a materiaallijst, a booked vordering, a collector
 * child or logged hours is refused by the server with 409 and Dutch copy naming
 * the blocker; that message reaches the caller as the Error's message, so the UI
 * must surface it rather than inventing its own.
 */
export async function deleteProjectFase(
  projectId: number,
  faseId: number,
): Promise<FaseArchiveResult> {
  return api<FaseArchiveResult>(
    `/api/projects/${projectId}/fases/${faseId}`,
    { method: 'DELETE' },
  );
}

/**
 * Mark (or unmark) a fase as interventie: the server sets/removes the Odoo
 * `Interventie` tag on the fase head. 422 INTERVENTIE_TAG_MISSING when the tag
 * does not exist in Odoo — the portal never creates it.
 */
export async function setFaseInterventie(
  projectId: number,
  faseId: number,
  interventie: boolean,
): Promise<{ id: number; interventie: boolean }> {
  return api<{ id: number; interventie: boolean }>(`/api/projects/${projectId}/fases/${faseId}/interventie`, {
    method: 'PUT',
    body: JSON.stringify({ interventie }),
  });
}

/** Does the Odoo `Interventie` tag exist? (The fase dialog disables its checkbox when not.) */
export async function fetchInterventieTagAvailable(): Promise<boolean> {
  const res = await api<{ available: boolean }>('/api/fases/interventie-tag');
  return res.available;
}
